import React, { useState, useEffect } from 'react';
import { Save, Upload, AlertTriangle, FileText } from 'lucide-react';
import { supabase } from '../../utils/supabase';
import toast from 'react-hot-toast';

interface SiteSettings {
  site_title: string;
  logo_url: string | null;
  favicon_url: string | null;
  terms_of_service: string;
  privacy_policy: string;
  meta_description: string;
  meta_image_url: string | null;
  meta_keywords: string;
}

export function SiteSettings() {
  const [settings, setSettings] = useState<SiteSettings>({
    site_title: 'Post Captions Lab',
    logo_url: null,
    favicon_url: null,
    terms_of_service: '',
    privacy_policy: '',
    meta_description: 'Transform your videos into viral LinkedIn posts with AI-powered captions',
    meta_image_url: null,
    meta_keywords: 'video captions, linkedin posts, ai transcription, social media'
  });
  const [loading, setLoading] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [faviconFile, setFaviconFile] = useState<File | null>(null);
  const [metaImageFile, setMetaImageFile] = useState<File | null>(null);
  const [activeTab, setActiveTab] = useState<'general' | 'legal' | 'meta'>('general');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('*')
        .eq('id', 1)
        .single();

      if (error) throw error;
      if (data) {
        setSettings(data);
        updateMetaTags(data);
      }
    } catch (error) {
      console.error('Error fetching site settings:', error);
      toast.error('Failed to load site settings');
    }
  };

  const updateMetaTags = (data: SiteSettings) => {
    // Update title
    document.title = data.site_title;
    
    // Update meta description
    const descriptionTag = document.querySelector('meta[name="description"]');
    if (descriptionTag) {
      descriptionTag.setAttribute('content', data.meta_description);
    }

    // Update Open Graph tags
    const ogTitleTag = document.querySelector('meta[property="og:title"]');
    const ogDescTag = document.querySelector('meta[property="og:description"]');
    const ogImageTag = document.querySelector('meta[property="og:image"]');
    
    if (ogTitleTag) ogTitleTag.setAttribute('content', data.site_title);
    if (ogDescTag) ogDescTag.setAttribute('content', data.meta_description);
    if (ogImageTag && data.meta_image_url) ogImageTag.setAttribute('content', data.meta_image_url);

    // Update Twitter Card tags
    const twitterTitleTag = document.querySelector('meta[property="twitter:title"]');
    const twitterDescTag = document.querySelector('meta[property="twitter:description"]');
    const twitterImageTag = document.querySelector('meta[property="twitter:image"]');
    
    if (twitterTitleTag) twitterTitleTag.setAttribute('content', data.site_title);
    if (twitterDescTag) twitterDescTag.setAttribute('content', data.meta_description);
    if (twitterImageTag && data.meta_image_url) twitterImageTag.setAttribute('content', data.meta_image_url);
  };

  const handleFileUpload = async (file: File, type: 'logo' | 'favicon' | 'meta'): Promise<string> => {
    try {
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      const allowedTypes = ['png', 'jpg', 'jpeg', 'svg'];
      
      if (!fileExt || !allowedTypes.includes(fileExt)) {
        throw new Error('Invalid file type. Only PNG, JPEG, and SVG files are allowed.');
      }

      // Create a more organized file path
      const fileName = `${type}/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      
      // Upload the file
      const { error: uploadError } = await supabase.storage
        .from('site-assets')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw new Error('Failed to upload file. Please try again.');
      }

      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from('site-assets')
        .getPublicUrl(fileName);

      if (!publicUrl) {
        throw new Error('Failed to get public URL for uploaded file');
      }

      return publicUrl;
    } catch (error) {
      console.error('File upload error:', error);
      throw error instanceof Error ? error : new Error('Failed to upload file');
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      let updates: Partial<SiteSettings> = {
        site_title: settings.site_title,
        terms_of_service: settings.terms_of_service,
        privacy_policy: settings.privacy_policy,
        meta_description: settings.meta_description,
        meta_keywords: settings.meta_keywords
      };

      if (logoFile) {
        const logoUrl = await handleFileUpload(logoFile, 'logo');
        updates.logo_url = logoUrl;
      }

      if (faviconFile) {
        const faviconUrl = await handleFileUpload(faviconFile, 'favicon');
        updates.favicon_url = faviconUrl;
      }

      if (metaImageFile) {
        const metaImageUrl = await handleFileUpload(metaImageFile, 'meta');
        updates.meta_image_url = metaImageUrl;
      }

      const { error } = await supabase
        .from('site_settings')
        .update(updates)
        .eq('id', 1);

      if (error) throw error;

      if (updates.favicon_url) {
        const favicon = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
        if (favicon) {
          favicon.href = updates.favicon_url;
        }
      }

      updateMetaTags({ ...settings, ...updates });
      toast.success('Site settings updated successfully');
      fetchSettings();
    } catch (error) {
      console.error('Error saving site settings:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to save site settings');
    } finally {
      setLoading(false);
      setLogoFile(null);
      setFaviconFile(null);
      setMetaImageFile(null);
    }
  };

  return (
    <div className="bg-card shadow rounded-lg p-6 border">
      <h3 className="text-lg font-medium text-foreground mb-6">Site Customization</h3>
      
      <div className="mb-6 border-b border-border">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('general')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'general'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            }`}
          >
            General Settings
          </button>
          <button
            onClick={() => setActiveTab('meta')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'meta'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            }`}
          >
            Meta & SEO
          </button>
          <button
            onClick={() => setActiveTab('legal')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'legal'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            }`}
          >
            Legal Documents
          </button>
        </nav>
      </div>

      {activeTab === 'general' && (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-foreground">
              Website Title
            </label>
            <input
              type="text"
              value={settings.site_title}
              onChange={(e) => setSettings({ ...settings, site_title: e.target.value })}
              className="mt-1 block w-full rounded-md border-input shadow-sm focus:border-primary focus:ring-primary bg-background text-foreground"
              placeholder="Post Captions Lab"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground">
              Website Logo
            </label>
            <div className="mt-1 flex items-center gap-4">
              {settings.logo_url && (
                <img
                  src={settings.logo_url}
                  alt="Website Logo"
                  className="h-10 w-auto object-contain bg-white p-1 rounded"
                />
              )}
              <label className="cursor-pointer inline-flex items-center px-4 py-2 border border-input rounded-md shadow-sm text-sm font-medium text-foreground bg-background hover:bg-secondary">
                <Upload className="w-4 h-4 mr-2" />
                Upload Logo
                <input
                  type="file"
                  className="hidden"
                  accept=".png,.jpg,.jpeg,.svg"
                  onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                />
              </label>
              {logoFile && (
                <span className="text-sm text-muted-foreground">
                  Selected: {logoFile.name}
                </span>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground">
              Favicon
            </label>
            <div className="mt-1 flex items-center gap-4">
              {settings.favicon_url && (
                <img
                  src={settings.favicon_url}
                  alt="Favicon"
                  className="h-8 w-8 object-contain bg-white p-1 rounded"
                />
              )}
              <label className="cursor-pointer inline-flex items-center px-4 py-2 border border-input rounded-md shadow-sm text-sm font-medium text-foreground bg-background hover:bg-secondary">
                <Upload className="w-4 h-4 mr-2" />
                Upload Favicon
                <input
                  type="file"
                  className="hidden"
                  accept=".png,.jpg,.jpeg,.svg"
                  onChange={(e) => setFaviconFile(e.target.files?.[0] || null)}
                />
              </label>
              {faviconFile && (
                <span className="text-sm text-muted-foreground">
                  Selected: {faviconFile.name}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'meta' && (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-foreground">
              Meta Description
            </label>
            <textarea
              value={settings.meta_description}
              onChange={(e) => setSettings({ ...settings, meta_description: e.target.value })}
              className="mt-1 block w-full rounded-md border-input shadow-sm focus:border-primary focus:ring-primary bg-background text-foreground"
              rows={3}
              placeholder="Enter a description for search engines and social media..."
            />
            <p className="mt-1 text-sm text-muted-foreground">
              This description appears in search results and social media shares.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground">
              Meta Keywords
            </label>
            <input
              type="text"
              value={settings.meta_keywords}
              onChange={(e) => setSettings({ ...settings, meta_keywords: e.target.value })}
              className="mt-1 block w-full rounded-md border-input shadow-sm focus:border-primary focus:ring-primary bg-background text-foreground"
              placeholder="video captions, linkedin posts, ai transcription..."
            />
            <p className="mt-1 text-sm text-muted-foreground">
              Comma-separated keywords for SEO purposes.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground">
              Social Media Preview Image
            </label>
            <div className="mt-1 flex items-center gap-4">
              {settings.meta_image_url && (
                <img
                  src={settings.meta_image_url}
                  alt="Social Media Preview"
                  className="h-32 w-auto object-cover rounded bg-white p-1"
                />
              )}
              <div className="space-y-2">
                <label className="cursor-pointer inline-flex items-center px-4 py-2 border border-input rounded-md shadow-sm text-sm font-medium text-foreground bg-background hover:bg-secondary">
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Preview Image
                  <input
                    type="file"
                    className="hidden"
                    accept=".png,.jpg,.jpeg"
                    onChange={(e) => setMetaImageFile(e.target.files?.[0] || null)}
                  />
                </label>
                {metaImageFile && (
                  <p className="text-sm text-muted-foreground">
                    Selected: {metaImageFile.name}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  Recommended size: 1200x630 pixels for optimal social media sharing
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'legal' && (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-foreground">
              Terms of Service
            </label>
            <div className="mt-1">
              <textarea
                value={settings.terms_of_service}
                onChange={(e) => setSettings({ ...settings, terms_of_service: e.target.value })}
                rows={20}
                className="mt-1 block w-full rounded-md border-input shadow-sm focus:border-primary focus:ring-primary font-mono text-sm bg-background text-foreground"
              />
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Supports Markdown formatting
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground">
              Privacy Policy
            </label>
            <div className="mt-1">
              <textarea
                value={settings.privacy_policy}
                onChange={(e) => setSettings({ ...settings, privacy_policy: e.target.value })}
                rows={20}
                className="mt-1 block w-full rounded-md border-input shadow-sm focus:border-primary focus:ring-primary font-mono text-sm bg-background text-foreground"
              />
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Supports Markdown formatting
            </p>
          </div>
        </div>
      )}

      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSave}
          disabled={loading}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
        >
          <Save className="w-4 h-4 mr-2" />
          {loading ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}